import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const GAP = 8;

/**
 * Popover component - Mostra un popup vicino al trigger.
 * Su mobile (e ovunque) il contenuto viene renderizzato in portal (document.body) con position:fixed
 * così non viene tagliato da overflow:hidden dei genitori (es. Gestione Clienti - WEBAPP-033).
 */
export default function Popover({ trigger, content, title, placement = 'bottom' }) {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);

    const getPosition = () => {
        if (!triggerRef.current) return { top: 0, left: 0 };
        const rect = triggerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        switch (placement) {
            case 'top':
                return { top: rect.top - GAP, left: centerX };
            case 'left':
                return { top: centerY, left: rect.left - GAP };
            case 'right':
                return { top: centerY, left: rect.right + GAP };
            case 'bottom':
            default:
                return { top: rect.bottom + GAP, left: centerX };
        }
    };

    const handleToggle = () => {
        if (!isOpen) setPosition(getPosition());
        setIsOpen((prev) => !prev);
    };

    useEffect(() => {
        function handleEscape(event) {
            if (event.key === 'Escape') setIsOpen(false);
        }
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen]);

    const getTransform = () => {
        switch (placement) {
            case 'top': return 'translate(-50%, -100%)';
            case 'left': return 'translate(-100%, -50%)';
            case 'right': return 'translate(0, -50%)';
            case 'bottom':
            default: return 'translate(-50%, 0)';
        }
    };

    const popoverContent = isOpen && (
        <>
            <div
                className="fixed inset-0 z-[9998]"
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
            />
            <div
                className="fixed z-[9999] animate-in fade-in-0 zoom-in-95 duration-200 max-h-[85vh] overflow-y-auto"
                style={{
                    top: position.top,
                    left: position.left,
                    transform: getTransform(),
                }}
            >
                <div className="min-w-[280px] max-w-[min(400px,calc(100vw-24px))] overflow-hidden rounded-2xl border border-[#dad8e5] bg-[#eeebf5] shadow-xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                    {title && (
                        <div className="flex items-start justify-between gap-2 border-b border-[#dad8e5] bg-[#dad8e5] px-4 py-3 dark:border-white/10 dark:bg-gray-800">
                            <h3 className="font-open-sans pr-2 text-base font-semibold text-[#3a3a3a] dark:text-white">
                                {title}
                            </h3>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                                className="flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-lg text-[#3a3a3a]/70 transition-colors hover:text-[#3a3a3a] dark:text-white/75 dark:hover:text-white"
                                aria-label="Chiudi"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    )}
                    <div className={`font-open-sans text-sm leading-relaxed text-[#3a3a3a] dark:text-white/95 ${title ? 'px-4 py-4' : 'p-4'}`}>
                        {content}
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div className="relative inline-block" ref={triggerRef}>
            <div onClick={handleToggle}>
                {trigger}
            </div>
            {typeof document !== 'undefined' && popoverContent && createPortal(popoverContent, document.body)}
        </div>
    );
}
